import type { paths } from "../deferred-api/contracts";
import { permissionCodes } from "../generated-api/permissions";
import { assertRequestAuthority, captureRequestAuthority } from "./authority";
import { apiClient } from "./client";
import { throwApiError as fail } from "./response-error";
import {
  clearCyraSession,
  getInstallationReference,
  readSessionCredentials,
  setCyraSession,
} from "./session-credentials";
import { sessionHeaders } from "./session-headers";

export type CurrentActor =
  paths["/v1/admin/access"]["get"]["responses"][200]["content"]["application/json"];
export const adminPermissions = permissionCodes;
export type AdminPermission = CurrentActor["permissions"][number];
const adminPermissionSet = new Set<string>(adminPermissions);

export async function createCyraSession(firebaseToken: string): Promise<string> {
  const result = await apiClient.POST("/v1/auth/session", {
    body: { installationReference: getInstallationReference() },
    params: { header: { authorization: `Bearer ${firebaseToken}` } },
  });
  if (!result.data) return fail(result.error, result.response);
  setCyraSession(result.data.sessionId);
  return result.data.sessionId;
}

export function validateCyraSession(
  signal = AbortSignal.timeout(5_000),
): Promise<CurrentActor | null> {
  const authority = captureRequestAuthority();
  // Bound identity-token retrieval as well as fetch. A stalled SDK must not keep old authority
  // visible indefinitely. Late completion has no authority-changing side effects.
  return new Promise((resolve, reject) => {
    const aborted = () =>
      reject(signal.reason ?? new DOMException("Authority validation aborted.", "AbortError"));
    if (signal.aborted) {
      aborted();
      return;
    }
    signal.addEventListener("abort", aborted, { once: true });
    const validate = async (): Promise<CurrentActor | null> => {
      const credentials = await readSessionCredentials();
      assertRequestAuthority(authority);
      if (!credentials) return null;
      signal.throwIfAborted();
      const header = await sessionHeaders();
      assertRequestAuthority(authority);
      signal.throwIfAborted();
      const access = await apiClient.GET("/v1/admin/access", {
        params: { header: { ...header, "x-cyra-admin-permission-set": "phase-7d" } },
        signal,
      });
      assertRequestAuthority(authority);
      if (access.response && (access.response.status === 429 || access.response.status >= 500))
        return fail(access.error, access.response);
      if (
        !access.data ||
        !Number.isSafeInteger(access.data.authorityEpoch) ||
        access.data.authorityEpoch < 1 ||
        access.data.sessionId !== credentials.sessionId ||
        !access.data.userId ||
        !validCatalogueScope(access.data.catalogueScope) ||
        !access.data.permissions.every((permission) => adminPermissionSet.has(permission))
      )
        return null;
      return access.data;
    };
    void validate()
      .then(resolve, reject)
      .finally(() => signal.removeEventListener("abort", aborted));
  });
}

function validCatalogueScope(value: unknown): value is CurrentActor["catalogueScope"] {
  if (!value || typeof value !== "object" || !("kind" in value)) return false;
  if (value.kind === "PLATFORM_ALL") return Object.keys(value).length === 1;
  if (value.kind === "PARTNER_SET" && Object.keys(value).length === 2 && "partnerIds" in value)
    return (
      Array.isArray(value.partnerIds) &&
      value.partnerIds.length <= 1_000 &&
      value.partnerIds.every((id) => typeof id === "string" && id.length >= 1 && id.length <= 64) &&
      new Set(value.partnerIds).size === value.partnerIds.length
    );
  if (value.kind !== "PARTNER_EXPERIENCE_SET" || !("grants" in value)) return false;
  return (
    Object.keys(value).length === 2 &&
    Array.isArray(value.grants) &&
    value.grants.length <= 100 &&
    value.grants.every(
      (grant) =>
        grant &&
        typeof grant === "object" &&
        Object.keys(grant).length === 3 &&
        "partnerId" in grant &&
        typeof grant.partnerId === "string" &&
        "destinationId" in grant &&
        typeof grant.destinationId === "string" &&
        "experienceIds" in grant &&
        Array.isArray(grant.experienceIds) &&
        grant.experienceIds.length >= 1 &&
        grant.experienceIds.length <= 100 &&
        grant.experienceIds.every(
          (id: unknown) => typeof id === "string" && id.length >= 1 && id.length <= 64,
        ) &&
        new Set(grant.experienceIds).size === grant.experienceIds.length,
    )
  );
}

export async function getEnrollmentEligibility(): Promise<{ mfaEnrolled: boolean }> {
  const credentials = await readSessionCredentials();
  if (!credentials) {
    throw new Error("The first-factor Cyra session is missing.");
  }
  const result = await apiClient.GET("/v1/admin/enrollment-eligibility", {
    params: { header: await sessionHeaders() },
  });
  if (!result.data) return fail(result.error, result.response);
  return { mfaEnrolled: result.data.mfaEnrolled };
}

export async function revokeCurrentCyraSession(): Promise<void> {
  const credentials = await readSessionCredentials();
  if (!credentials) {
    clearCyraSession();
    return;
  }
  const result = await apiClient.DELETE("/v1/auth/session/current", {
    params: { header: await sessionHeaders() },
  });
  if (result.error) return fail(result.error, result.response);
  clearCyraSession();
}
